import React, { useEffect, useState } from "react";
import ChangesAdditionalCharges from "./ChangesAdditionalCharges2";
import RackRateDetails from "./RackRateDetails2";
import useAuth from "../../../components/hooks/useAuth";
import TaxChange from "./TaxChange2";
import ReservationSettings from "./ReservationSettings2";
import DistanceSettings from "./DistanceSettings2";
import ExtraAmount from "./ExtraAmount2";
import SecurityDepositSettings from "./SecurityDepositSettings2";
import { Button } from "@mui/material";
import colorConfigs from "../../../configs/colorConfigs";
import { changeReservationDetails } from "../../../services/rental";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";


type ChangesProps = {
  summaryData: any;
  reservationAdditionalCharge: any[];
  refreshTheAdjustment: () => void;
  refreshTheSummary: () => void;
  isCompletedOrCancelled: boolean;
  reservationId: number;
  locationFeeAmount: number;
  taxAmount: number;
  governmentTaxAmount: number;
  setLocationFeeAmount: (value: number) => void;
  setTaxAmount: (value: number) => void;
  setGovernmentTaxAmount: (value: number) => void;
  setPleaseWait: (value: boolean) => void;
};


export const Changes = ({
  summaryData,
  reservationAdditionalCharge,
  refreshTheAdjustment,
  refreshTheSummary,
  isCompletedOrCancelled,
  reservationId,
  locationFeeAmount,
  taxAmount,
  governmentTaxAmount,
  setLocationFeeAmount,
  setTaxAmount,
  setGovernmentTaxAmount,
  setPleaseWait
}: ChangesProps) => {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { auth }: any = useAuth();
  const { handleTenantError }: any = useTenant();
  const [hourlyRate, setHourlyRate] = useState<string>("");
  const [dailyRate, setDailyRate] = useState<string>("");
  const [weeklyRate, setWeeklyRate] = useState<string>("");
  const [yearlyRate, setYearlyRate] = useState<string>("");

  //Reservation Settings
  const [language, setLanguage] = useState<string>("English");
  const [renewalDisabled, setRenewalDisabled] = useState<boolean>(false);
  const [renewContractsXDaysInAdvance, setRenewContractsXDaysInAdvance] = useState<number>(0);
  const [forceRenewalToXDays, setForceRenewalToXDays] = useState<number>(0);
  const [renewContractXHoursAfterPlannedReturnTime, setRenewContractXHoursAfterPlannedReturnTime] = useState<number>(0);
  const [bookingHappendAfterSaleEmailSent, setBookingHappendAfterSaleEmailSent] = useState<boolean>(false);
  const [isThisWalkInCustomer, setIsThisWalkInCustomer] = useState<boolean>(false);

  //Additional Charges Settings
  const [reservationAdditionalCharges, setReservationAdditionalCharges] = useState<any[]>([]);

  //Distance Settings
  const [chargeDistanceWhenLimitIsZero, setChargeDistanceWhenLimitIsZero] = useState<boolean>(false);
  const [totalDistanceLimit, setTotalDistanceLimit] = useState<number>(0);
  const [distanceLimitPerDay, setDistanceLimitPerDay] = useState<number>(0);
  const [distanceLimitPerWeek, setDistanceLimitPerWeek] = useState<number>(0);
  const [distanceLimitPerMonth, setDistanceLimitPerMonth] = useState<number>(0);

  //Extra Amount
  const [isCalendarDayBooking, setIsCalendarDayBooking] = useState<boolean>(false);
  const [manualDiscount, setManualDiscount] = useState<number>(0);
  const [notIncludeVehicleDamagesInLocationFee, setNotIncludeVehicleDamagesInLocationFee] = useState<boolean>(false);
  const [notIncludeVehicleDamagesInSalesTax, setNotIncludeVehicleDamagesInSalesTax] = useState<boolean>(false);
  const [includeVehicleDamagesInRevenue, setIncludeVehicleDamagesInRevenue] = useState<boolean>(true);
  const [includeExternalChargesInLocationFee, setIncludeExternalChargesInLocationFee] = useState<boolean>(true);
  const [includeExternalChargesInTaxes, setIncludeExternalChargesInTaxes] = useState<boolean>(true);
  const [notIncludeLocationFeeInTaxCalculation, setNotIncludeLocationFeeInTaxCalculation] = useState<boolean>(true);
  const [maxHoursBeforeChargingAnExtraDay, setMaxHoursBeforeChargingAnExtraDay] = useState<number>(0);
  const [maxHoursBeforeAdditionalChargingAnExtraDay, setMaxHoursBeforeAdditionalChargingAnExtraDay] = useState<number>(0);
  const [disableAllEmails, setDisableAllEmails] = useState<boolean>(false);

  //Security Deposit Settings
  const [securityDepositId, setSecurityDepositId] = useState<number>(0);
  const [wasSecurityDepositManuallyChanged, setWasSecurityDepositManuallyChanged] = useState<boolean>(false);
  const [wasExcessChargedFromSecurityDeposit, setWasExcessChargedFromSecurityDeposit] = useState<boolean>(false);
  const [securityDepositPaid, setSecurityDepositPaid] = useState<boolean>(false);
  const [securityDepositRefunded, setSecurityDepositRefunded] = useState<boolean>(false);

  useEffect(() => {
    if (reservationAdditionalCharge) {
      setReservationAdditionalCharges(reservationAdditionalCharge);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationAdditionalCharge]);

  useEffect(() => {
    setHourlyRate(summaryData?.hourlyRate);
    setDailyRate(summaryData?.dailyRate);
    setWeeklyRate(summaryData?.weeklyRate);
    setYearlyRate(summaryData?.monthlyRate);

    setLanguage(summaryData?.language);
    setRenewalDisabled(summaryData?.renewalDisabled);
    setRenewContractsXDaysInAdvance(summaryData?.renewContractsXDaysInAdvance);
    setForceRenewalToXDays(summaryData?.forceRenewalToXDays);
    setRenewContractXHoursAfterPlannedReturnTime(summaryData?.renewContractXHoursAfterPlannedReturnTime);
    setBookingHappendAfterSaleEmailSent(summaryData?.bookingHappendAfterSaleEmailSent);
    setIsThisWalkInCustomer(summaryData?.isThisWalkInCustomer);

    setChargeDistanceWhenLimitIsZero(summaryData?.chargeDistanceWhenLimitIsZero);
    setTotalDistanceLimit(summaryData?.totalDistanceLimit);
    setDistanceLimitPerDay(summaryData?.distanceLimitPerDay);
    setDistanceLimitPerWeek(summaryData?.distanceLimitPerWeek);
    setDistanceLimitPerMonth(summaryData?.distanceLimitPerMonth);

    setIsCalendarDayBooking(summaryData?.isCalendarDayBooking);
    setManualDiscount(summaryData?.manualDiscount);
    setNotIncludeVehicleDamagesInLocationFee(summaryData?.notIncludeVehicleDamagesInLocationFee);
    setNotIncludeVehicleDamagesInSalesTax(summaryData?.notIncludeVehicleDamagesInSalesTax);
    setIncludeVehicleDamagesInRevenue(summaryData?.includeVehicleDamagesInRevenue);
    setIncludeExternalChargesInLocationFee(summaryData?.includeExternalChargesInLocationFee);
    setIncludeExternalChargesInTaxes(summaryData?.includeExternalChargesInTaxes);
    setNotIncludeLocationFeeInTaxCalculation(summaryData?.notIncludeLocationFeeInTaxCalculation);
    setMaxHoursBeforeChargingAnExtraDay(summaryData?.maxHoursBeforeChargingAnExtraDay);
    setMaxHoursBeforeAdditionalChargingAnExtraDay(summaryData?.maxHoursBeforeAdditionalChargingAnExtraDay);
    setDisableAllEmails(summaryData?.disableAllEmails);

    setSecurityDepositId(summaryData?.securityDepositId);
    setWasSecurityDepositManuallyChanged(summaryData?.wasSecurityDepositManuallyChanged);
    setWasExcessChargedFromSecurityDeposit(summaryData?.wasExcessChargedFromSecurityDeposit);
    setSecurityDepositPaid(summaryData?.securityDepositPaid);
    setSecurityDepositRefunded(summaryData?.securityDepositRefunded);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryData])

  const saveTheChange = () => {
    if (!isCompletedOrCancelled) {
      setPleaseWait(true);

      changeReservationDetails(
        {
          hourlyRate: hourlyRate,
          dailyRate: dailyRate,
          weeklyRate: weeklyRate,
          monthlyRate: yearlyRate,
          wasRateManuallyChanged: true,
          reservationAdditionalCharges: reservationAdditionalCharges?.map((item) => ({
            id: item?.id,
            reservatinId: reservationId,
            additionalChargeByBranchId: item?.additionalChargeByBranchId,
            quantity: +item?.quantity,
            basePrice: item?.basePrice
          })
          ),
          locationFeePercentage: locationFeeAmount,
          taxPercentage: taxAmount,
          governmentTaxPercentage: governmentTaxAmount,
          language: language,
          renewalDisabled: renewalDisabled,
          renewContractsXDaysInAdvance: renewContractsXDaysInAdvance,
          forceRenewalToXDays: forceRenewalToXDays,
          renewContractXHoursAfterPlannedReturnTime: renewContractXHoursAfterPlannedReturnTime,
          bookingHappendAfterSaleEmailSent: bookingHappendAfterSaleEmailSent,
          isThisWalkInCustomer: isThisWalkInCustomer,
          chargeDistanceWhenLimitIsZero: chargeDistanceWhenLimitIsZero,
          totalDistanceLimit: totalDistanceLimit,
          distanceLimitPerDay: distanceLimitPerDay,
          distanceLimitPerWeek: distanceLimitPerWeek,
          distanceLimitPerMonth: distanceLimitPerMonth,
          isCalendarDayBooking: isCalendarDayBooking,
          manualDiscount: manualDiscount,
          notIncludeVehicleDamagesInLocationFee: notIncludeVehicleDamagesInLocationFee,
          includeVehicleDamagesInRevenue: includeVehicleDamagesInRevenue,
          includeExternalChargesInLocationFee: includeExternalChargesInLocationFee,
          notIncludeLocationFeeInTaxCalculation: notIncludeLocationFeeInTaxCalculation,
          notIncludeVehicleDamagesInSalesTax: notIncludeVehicleDamagesInSalesTax,
          includeExternalChargesInTaxes: includeExternalChargesInTaxes,
          maxHoursBeforeChargingAnExtraDay: maxHoursBeforeChargingAnExtraDay,
          maxHoursBeforeAdditionalChargingAnExtraDay: maxHoursBeforeAdditionalChargingAnExtraDay,
          disableAllEmails: disableAllEmails,
          securityDepositId: securityDepositId,
          wasSecurityDepositManuallyChanged: wasSecurityDepositManuallyChanged,
          securityDepositPaid: securityDepositPaid,
          wasExcessChargedFromSecurityDeposit: wasExcessChargedFromSecurityDeposit,
          securityDepositRefunded: securityDepositRefunded
        },
        reservationId,
        auth?.id,
        tenantId
      )
        .then(() => {
          refreshTheSummary();
          refreshTheAdjustment();
          handleTenantError(null);
        })
        .catch(error => {
          setPleaseWait(false);
          handleTenantError(error);
        })
    }
  }


  return (
    <>
      <div className="w-full">
        <RackRateDetails
          summaryData={summaryData}
          hourlyRate={hourlyRate}
          setHourlyRate={setHourlyRate}
          dailyRate={dailyRate}
          setDailyRate={setDailyRate}
          weeklyRate={weeklyRate}
          setWeeklyRate={setWeeklyRate}
          yearlyRate={yearlyRate}
          setYearlyRate={setYearlyRate}
        />

        <ChangesAdditionalCharges
          reservationAdditionalCharges={reservationAdditionalCharges}
          setReservationAdditionalCharges={setReservationAdditionalCharges}
        />

        <ReservationSettings
          language={language}
          setLanguage={setLanguage}
          renewalDisabled={renewalDisabled}
          setRenewalDisabled={setRenewalDisabled}
          renewContractsXDaysInAdvance={renewContractsXDaysInAdvance}
          setRenewContractsXDaysInAdvance={setRenewContractsXDaysInAdvance}
          forceRenewalToXDays={forceRenewalToXDays}
          setForceRenewalToXDays={setForceRenewalToXDays}
          renewContractXHoursAfterPlannedReturnTime={renewContractXHoursAfterPlannedReturnTime}
          setRenewContractXHoursAfterPlannedReturnTime={setRenewContractXHoursAfterPlannedReturnTime}
          bookingHappendAfterSaleEmailSent={bookingHappendAfterSaleEmailSent}
          setBookingHappendAfterSaleEmailSent={setBookingHappendAfterSaleEmailSent}
          isThisWalkInCustomer={isThisWalkInCustomer}
          setIsThisWalkInCustomer={setIsThisWalkInCustomer}
        />

        <DistanceSettings
          chargeDistanceWhenLimitIsZero={chargeDistanceWhenLimitIsZero}
          setChargeDistanceWhenLimitIsZero={setChargeDistanceWhenLimitIsZero}
          totalDistanceLimit={totalDistanceLimit}
          setTotalDistanceLimit={setTotalDistanceLimit}
          distanceLimitPerDay={distanceLimitPerDay}
          setDistanceLimitPerDay={setDistanceLimitPerDay}
          distanceLimitPerWeek={distanceLimitPerWeek}
          setDistanceLimitPerWeek={setDistanceLimitPerWeek}
          distanceLimitPerMonth={distanceLimitPerMonth}
          setDistanceLimitPerMonth={setDistanceLimitPerMonth}
        />

        <ExtraAmount
          isCalendarDayBooking={isCalendarDayBooking}
          setIsCalendarDayBooking={setIsCalendarDayBooking}
          manualDiscount={manualDiscount}
          setManualDiscount={setManualDiscount}
          notIncludeVehicleDamagesInLocationFee={notIncludeVehicleDamagesInLocationFee}
          setNotIncludeVehicleDamagesInLocationFee={setNotIncludeVehicleDamagesInLocationFee}
          notIncludeVehicleDamagesInSalesTax={notIncludeVehicleDamagesInSalesTax}
          setNotIncludeVehicleDamagesInSalesTax={setNotIncludeVehicleDamagesInSalesTax}
          includeVehicleDamagesInRevenue={includeVehicleDamagesInRevenue}
          setIncludeVehicleDamagesInRevenue={setIncludeVehicleDamagesInRevenue}
          includeExternalChargesInLocationFee={includeExternalChargesInLocationFee}
          setIncludeExternalChargesInLocationFee={setIncludeExternalChargesInLocationFee}
          includeExternalChargesInTaxes={includeExternalChargesInTaxes}
          setIncludeExternalChargesInTaxes={setIncludeExternalChargesInTaxes}
          notIncludeLocationFeeInTaxCalculation={notIncludeLocationFeeInTaxCalculation}
          setNotIncludeLocationFeeInTaxCalculation={setNotIncludeLocationFeeInTaxCalculation}
          maxHoursBeforeChargingAnExtraDay={maxHoursBeforeChargingAnExtraDay}
          setMaxHoursBeforeChargingAnExtraDay={setMaxHoursBeforeChargingAnExtraDay}
          maxHoursBeforeAdditionalChargingAnExtraDay={maxHoursBeforeAdditionalChargingAnExtraDay}
          setMaxHoursBeforeAdditionalChargingAnExtraDay={setMaxHoursBeforeAdditionalChargingAnExtraDay}
          disableAllEmails={disableAllEmails}
          setDisableAllEmails={setDisableAllEmails}
        />

        <SecurityDepositSettings
          securityDepositId={securityDepositId}
          setSecurityDepositId={setSecurityDepositId}
          wasSecurityDepositManuallyChanged={wasSecurityDepositManuallyChanged}
          setWasSecurityDepositManuallyChanged={setWasSecurityDepositManuallyChanged}
          wasExcessChargedFromSecurityDeposit={wasExcessChargedFromSecurityDeposit}
          setWasExcessChargedFromSecurityDeposit={setWasExcessChargedFromSecurityDeposit}
          securityDepositPaid={securityDepositPaid}
          setSecurityDepositPaid={setSecurityDepositPaid}
          securityDepositRefunded={securityDepositRefunded}
          setSecurityDepositRefunded={setSecurityDepositRefunded}
        />

        <TaxChange
          locationFeeAmount={locationFeeAmount}
          taxAmount={taxAmount}
          governmentTaxAmount={governmentTaxAmount}
          setLocationFeeAmount={setLocationFeeAmount}
          setTaxAmount={setTaxAmount}
          setGovernmentTaxAmount={setGovernmentTaxAmount}
        />

        <div className="w-full mt-4">
          <Button variant="contained"
            sx={{
              backgroundColor: colorConfigs.btn.main.bg,
              fontSize: "10px"
            }}
            onClick={saveTheChange}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </>
  )
}